import React from 'react'
import 'project/polyfill'
import classNames from 'classnames'

import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import { Utils } from 'common/utils'
import { getPlans } from 'common/planData'
import { createClient } from '../prismicio'
import { getFAQList } from 'common/cms-sdk'
import { PlanSlug } from 'common/planSlugs'
import FAQList from 'components/util/FAQList'
import HeroBanner from 'components/hero/HeroBanner'
import { ItemListElements } from 'next-seo/lib/types'
import HeadingAndText from 'components/HeadingAndText'
import { appStrings } from 'common/strings/app-strings'
import { TFAQList, TPlanSummary } from 'common/cms-types'
import useWindowSize from 'components/util/useWindowSize'
import { I18NConfig } from 'next/dist/server/config-shared'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import SubscriptionPricing from 'components/SubscriptionPricing'
import { generateSrcSet } from 'project/image-proxy-web'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import { GooglePlayButton, AppStoreButton } from 'components/AppDownloadButtons'
import { HoxtonMixLogo } from 'components/icons'

export type AppPageType = {
  faqList: TFAQList
  plans: TPlanSummary[]
}
const AppPage: NextPageWithLayout<AppPageType> = ({ faqList, plans }) => {
  const windowSize = useWindowSize()
  const seoProps: SeoContainerProps['seoProps'] = {
    description: appStrings.heroTitle,
    openGraph: {
      url: 'https://www.hoxtonmix.com/mobile/',
    },
    title: appStrings.title,
  }

  const breadcrumb: ItemListElements[] = [
    homeBreadcrumb()[0],
    {
      item: 'https://www.hoxtonmix.com/mobile/',
      name: `${appStrings.title}`,
      position: 1,
    },
  ]

  const planSummary = plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!

  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <main className={'overflow-hidden'}>
        <HeroBanner
          robotImage={{
            alt: 'HaiRo™ robot mascot displaying mail interface',
            dimensions: {
              height: 1000,
              width: 827,
            },
            src: '/images/new/robot-mail-preview.png',
          }}
          cta={<SubscriptionPricing
            buttonAlign='start' showLink planSummary={planSummary} />}
          title='Access your business mail anywhere, anytime'
          description={
            <>
              <h2 className='fs-fluid-24-32 fw-semibold lh-114 mb-4'>
                {appStrings.subtitle}
              </h2>
              <p className='fs-fluid-18-24 fw-semibold lh-140'>
                Instantly view, search, and share your business post with our
                mobile app — available on iOS and Android.
              </p>
            </>
          }
          logo={<HoxtonMixLogo className='mb-5 mb-lg-8' />}
        />
        <div className='container'>
          <HeadingAndText
            headingSize={windowSize.isLgOrHigher ? 1 : 2}
            title={appStrings.title}
            classesForTitle={classNames(
              'mt-14 mt-lg-22 mt-xl-42 mb-lg-3 mb-2 px-lg-38 px-4 text-bold',
            )}
            subtitle={null}
          >
            <div className='d-flex justify-content-center'>
              <AppStoreButton />
              <GooglePlayButton />
            </div>
            <p
              className={
                'text-medium3 text-center mt-3 mt-lg-0 mb-0 px-14 px-lg-0'
              }
            >
              {appStrings.subtitle}
            </p>
          </HeadingAndText>
          <div className='row mx-auto mt-lg-25 mt-xl-50'>
            <div className={'col-md-6'}>
              <div
                className={
                  'h-100 d-flex justify-content-center justify-content-sm-start mt-14'
                }
              >
                <div>
                  <p
                    className='fs-fluid-24-32 text-semibold mb-1 mb-lg-3'
                    dangerouslySetInnerHTML={{
                      __html: appStrings.appBenefits,
                    }}
                  />
                  <h2
                    className={classNames(
                      'text-semibold pe-8 pe-lg-0',
                      windowSize.isLgOrHigher ? 'h1' : 'h2',
                    )}
                    dangerouslySetInnerHTML={{
                      __html: appStrings.manageYourPhysicalMailWithEase,
                    }}
                  />
                </div>
              </div>
            </div>
            <div className='col-md-6 px-0 my-6 my-lg-0'>
              <img
                loading='lazy'
                {...generateSrcSet(
                  '/images/app/HM-App-Bot-Phone 2.png',
                  715,
                  704,
                )}
                height={704}
                width={715}
                alt={'HM App Bot Phone'}
                className={'img-fluid'}
              />
            </div>
          </div>
          <div className='row'>
            <div className={'col-md-6 pe-lg-22 pe-xxl-36'}>
              <p
                className='h4 h4--mobile-lh2 text-muted text-lg-semibold mb-12 mb-md-18 mb-lg-12'
                dangerouslySetInnerHTML={{
                  __html: appStrings.p1,
                }}
              />
              <p
                className='h4 h4--mobile-lh2 text-muted text-lg-semibold mb-12 mb-md-22'
                dangerouslySetInnerHTML={{
                  __html: appStrings.p2,
                }}
              />
              <p
                className='h4 h4--mobile-lh2 text-muted text-lg-semibold mb-12 mb-md-0'
                dangerouslySetInnerHTML={{
                  __html: appStrings.p3,
                }}
              />
            </div>
            <div className={'col-md-6 ps-lg-14 pe-lg-12 pe-xxl-24'}>
              <p
                className='h4 h4--mobile-lh2 text-muted text-lg-semibold mb-12 mb-md-18 mb-lg-12'
                dangerouslySetInnerHTML={{
                  __html: appStrings.p4,
                }}
              />
              <p
                className='h4 h4--mobile-lh2 text-muted text-lg-semibold mb-12 mb-md-18 mb-lg-12'
                dangerouslySetInnerHTML={{
                  __html: appStrings.p5,
                }}
              />

              <p
                className='h4 h4--mobile-lh2 text-muted text-lg-semibold'
                dangerouslySetInnerHTML={{
                  __html: appStrings.p6,
                }}
              />
            </div>
          </div>
          <div className='row flex-column-reverse flex-lg-row mx-auto mt-10 mt-lg-25 mt-xl-50'>
            <div className='col-lg-6 text-center px-0'>
              <img
                {...generateSrcSet('/images/app/HM_App-Bot 1.png', 535, 599)}
                height={599}
                width={535}
                alt={'HM App Bot'}
                className={'img-fluid'}
                loading='lazy'
              />
              {!windowSize.isLgOrHigher && (
                <>
                  <div className='d-flex justify-content-center gap-2'>
                    <AppStoreButton />
                    <GooglePlayButton />
                  </div>
                  <p
                    className={
                      'text-medium3 text-center mt-3 mt-lg-0 mb-0 px-14 px-lg-0'
                    }
                  >
                    {appStrings.requiresAnActiveVirtualOfficeSubscription}
                  </p>
                </>
              )}
            </div>
            <div className={'col-lg-6 px-0'}>
              <div
                className={
                  'h-100 d-flex justify-content-center justify-content-sm-start'
                }
              >
                <div className={'my-auto ms-lg-n38 ms-xxl-n54 pb-lg-12'}>
                  <h2
                    className={classNames(
                      'text-semibold',
                      windowSize.isLgOrHigher ? 'h1' : 'h2',
                    )}
                    dangerouslySetInnerHTML={{
                      __html:
                        appStrings.getFastEasyMobileAccessWithTheHoxtonMixApp,
                    }}
                  />
                  {windowSize.isLgOrHigher && (
                    <>
                      <div className='d-flex gap-2'>
                        <AppStoreButton />
                        <GooglePlayButton />
                      </div>
                      <p
                        className={
                          'text-medium3 mt-3 mt-lg-0 mb-0 px-14 px-lg-0'
                        }
                      >
                        {appStrings.requiresAnActiveVirtualOfficeSubscription}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          {faqList && (
            <div className='mb-6'>
              <FAQList
                title={faqList.data.title}
                subtitle={faqList.data.subtitle}
                slices={faqList.data.slices}
                expandAllButton
                expandAllButtonDesktopOnly
              />
            </div>
          )}
        </div>
        <div className={'spacer'} />
      </main>
    </SeoContainer>
  )
}

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)

    const faqList = await getFAQList(client, lang, 'mobile-app')
    const plans = await getPlans()
    return {
      props: {
        faqList,
        plans,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}

export default AppPage
